import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { useMemo } from "react";

import { useWorkerVisibleQualifications } from "../api/useWorkerVisibleQualifications";
import { ALL_QUALIFICATIONS_CATEGORY } from "./constants";
import { type QualificationsByCategories } from "./types";
import { useGetQualificationCategoriesConfig } from "./useGetQualificationCategoriesConfig";

export function useGetQualificationsByCategories() {
  const {
    data: qualifications,
    isSuccess,
    ...qualificationsQuery
  } = useWorkerVisibleQualifications();

  const config = useGetQualificationCategoriesConfig();

  const qualificationsByCategories = useMemo(() => {
    if (!isSuccess) {
      return undefined;
    }

    const qualificationsByCategories: Partial<QualificationsByCategories> = {};

    for (const category of Object.keys(config)) {
      qualificationsByCategories[category] = config[category]
        .map((qualificationName) => {
          const qualification = qualifications?.data.find(
            (qualification) => qualification.attributes.name === qualificationName
          );

          if (!isDefined(qualification)) {
            logError(APP_V2_APP_EVENTS.QUALIFICATION_CATEGORY_ENTRY_NOT_FOUND, {
              error: new Error(`Qualification ${qualificationName} not found`),
              metadata: {
                qualificationName,
              },
            });
          }

          return qualification;
        })
        .filter(isDefined);
    }

    return {
      ...qualificationsByCategories,
      [ALL_QUALIFICATIONS_CATEGORY]: qualifications?.data ?? [],
    };
  }, [qualifications?.data, config, isSuccess]);

  return {
    ...qualificationsQuery,
    isSuccess,
    data: qualificationsByCategories,
  };
}
